// ToolsPanel — the "Araçlar" (Tools) right-panel body.
//
// Rendered inside a compact floating card that mirrors the Scene panel: the
// four tools are ICON-ONLY buttons stacked vertically (tooltips on hover, no
// text labels). Selecting Border reveals its controls below a separator; the
// other three act immediately:
//   1. Crop        — in-canvas rectangle crop (CanvasCropOverlay) → upload → apply
//   2. Border      — reuses the existing box-style border settings (updatePluginToolbar)
//   3. RemoveBg    — self-hosted rembg via MW_API async job (api/image-editing/remove-background)
//   4. ChromaKey   — click the pipette → click the image (ColorPickOverlay) to sample
//                    a colour → chroma-key removal fires immediately. No sub-panel.
//
// Background removal / chroma key follow the same MW_API job pattern as
// ImageAnimatorModal: POST → { jobId } → poll api/image-editing/job/:jobId.
// The result URL is applied back onto the box via onApplyImageUrl(url).

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  LuCrop,
  LuSquare,
  LuImageOff,
  LuPipette,
  LuLoaderCircle,
  LuSlidersHorizontal,
  LuRotateCcw,
  LuSettings,
  LuFlipHorizontal2,
  LuFlipVertical2,
  LuChevronUp,
  LuChevronDown,
  LuBlend,
  LuMapPin,
  LuMove,
  LuX,
  LuCheck,
} from "react-icons/lu";
import { getToken } from "../../../common/environment_config";
import { approveJob } from "../../../common/server_utils.es6";
import { updatePluginToolbar, resizeBox } from "../../../common/actions";
import { SceneIconButton } from "./RightIconStrip";
import CanvasCropOverlay from "./CanvasCropOverlay";
import CanvasColorPickOverlay from "./CanvasColorPickOverlay";
import ColorPicker from "../common/color-picker/ColorPicker";
import LiveNumberInput from "../common/LiveNumberInput";
import HoldRepeatButton from "../common/HoldRepeatButton";
import { normalizeRotation, ROTATION_MIN, ROTATION_MAX } from "../../utils/rotation";
import MarksList from "../rich_plugins/marks_list/MarksList";
import { computeToolsPopoverPlacement } from "./toolsPopoverPlacement";
import { uploadCroppedBlob } from "./cropUpload";
import { isCreditRefusal } from "../../utils/insufficientBalance";

const PURPLE = "#a963f5";
const IDLE = "#475569";
const FONT = '"Inter", sans-serif';
const CHROMA_TOLERANCE = 30; // sensible default for solid-colour backgrounds

// ---- Image adjustments (Canva-style "Ayarlar") --------------------------
// Non-destructive: raw slider values live in structure.imageAdjust; the derived
// CSS `filter` string is written to style.filter, which EditorBox/VisorBox
// spread straight onto the box element (so it renders live in both editor and
// viewer, and is saved with the training). No server job — pure CSS.
//
// Only adjustments a CSS `filter` can honour faithfully are exposed. Highlights/
// shadows/whites/blacks, positive sharpness, clarity, vignette and colour-grade
// presets need per-pixel processing (a baked canvas / server job) and are left
// out on purpose rather than shipped as no-op sliders.
const DEFAULT_ADJUST = {
  temperature: 0, // -100 cool … +100 warm
  tint: 0, // -100 … +100 → hue-rotate
  brightness: 0, // -100 … +100
  contrast: 0, // -100 … +100
  vibrance: 0, // -100 … +100 (mild saturate)
  saturation: 0, // -100 … +100
  blur: 0, // 0 … 100
  invert: false,
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

// Compose a CSS filter string from the raw adjustment values. Returns "none"
// when everything is at default so the box carries no filter at all.
function buildFilterCss(a) {
  const v = { ...DEFAULT_ADJUST, ...(a || {}) };
  const parts = [];
  if (v.brightness) parts.push(`brightness(${(100 + v.brightness) / 100})`);
  if (v.contrast) parts.push(`contrast(${(100 + v.contrast) / 100})`);
  // Vibrance is treated as a gentler saturate, folded into the same saturate().
  const sat = 100 + v.saturation + v.vibrance / 2;
  if (sat !== 100) parts.push(`saturate(${Math.max(0, sat) / 100})`);
  // Warm side of temperature ≈ sepia; cool side ≈ a small blue hue shift.
  if (v.temperature > 0) parts.push(`sepia(${(v.temperature / 100) * 0.5})`);
  const hue = v.tint * 1.2 + (v.temperature < 0 ? v.temperature * 0.5 : 0);
  if (hue) parts.push(`hue-rotate(${Math.round(hue)}deg)`);
  if (v.blur > 0) parts.push(`blur(${((v.blur / 100) * 5).toFixed(2)}px)`);
  if (v.invert) parts.push("invert(1)");
  return parts.length ? parts.join(" ") : "none";
}

// ---- Colour filter presets (Canva-style "Filtreler") -------------------
// One-click look presets, grouped Natural / Warm / Cold. Each is a pure CSS
// `filter` string, composed together with the raw Adjust sliders into the same
// style.filter the box spreads onto its element — so a preset renders live in
// both editor and viewer, is saved with the training, and stacks on top of any
// manual adjustments without a server job. The selected key lives on
// structure.filterPreset; "none" = original image.
const FILTER_GROUPS = [
  {
    titleKey: "FilterGroupNatural",
    presets: [
      {
        key: "fresh",
        labelKey: "FilterFresh",
        css: "saturate(1.15) brightness(1.05) contrast(1.02)",
      },
      { key: "vivid", labelKey: "FilterVivid", css: "saturate(1.4) contrast(1.1)" },
      {
        key: "flint",
        labelKey: "FilterFlint",
        css: "grayscale(0.3) contrast(1.05) brightness(0.98)",
      },
      {
        key: "luna",
        labelKey: "FilterLuna",
        css: "brightness(1.1) contrast(0.95) saturate(0.85) hue-rotate(-6deg)",
      },
      {
        key: "sky",
        labelKey: "FilterSky",
        css: "saturate(1.1) hue-rotate(-10deg) brightness(1.03)",
      },
      { key: "noir", labelKey: "FilterNoir", css: "grayscale(1) contrast(1.1) brightness(0.95)" },
    ],
  },
  {
    titleKey: "FilterGroupWarm",
    presets: [
      {
        key: "tropic",
        labelKey: "FilterTropic",
        css: "saturate(1.5) hue-rotate(-8deg) contrast(1.05)",
      },
      { key: "sun", labelKey: "FilterSun", css: "sepia(0.25) saturate(1.3) brightness(1.08)" },
      {
        key: "latte",
        labelKey: "FilterLatte",
        css: "sepia(0.4) contrast(0.95) brightness(1.05) saturate(1.1)",
      },
      { key: "bronze", labelKey: "FilterBronze", css: "sepia(0.5) saturate(1.2) contrast(1.05)" },
      { key: "sand", labelKey: "FilterSand", css: "sepia(0.3) brightness(1.05) saturate(0.9)" },
      {
        key: "wine",
        labelKey: "FilterGrape",
        css: "sepia(0.3) hue-rotate(-20deg) saturate(1.3) contrast(1.05)",
      },
    ],
  },
  {
    titleKey: "FilterGroupCold",
    presets: [
      {
        key: "cd_scandi",
        labelKey: "FilterScandinavian",
        css: "brightness(1.08) contrast(0.95) saturate(0.8) hue-rotate(12deg)",
      },
      {
        key: "cd_nordic",
        labelKey: "FilterNordic",
        css: "brightness(1.05) saturate(0.85) hue-rotate(18deg)",
      },
      {
        key: "cd_astro",
        labelKey: "FilterAstro",
        css: "contrast(1.1) saturate(0.9) hue-rotate(25deg) brightness(0.98)",
      },
      {
        key: "cd_arctic",
        labelKey: "FilterArctic",
        css: "brightness(1.1) contrast(0.9) saturate(0.75) hue-rotate(15deg)",
      },
      {
        key: "cd_polar",
        labelKey: "FilterPolar",
        css: "contrast(1.05) saturate(0.85) hue-rotate(20deg) brightness(1.03)",
      },
      {
        key: "cd_tundra",
        labelKey: "FilterTundra",
        css: "brightness(1.02) saturate(0.8) hue-rotate(10deg) contrast(1.05)",
      },
    ],
  },
  {
    titleKey: "FilterGroupVivid",
    presets: [
      { key: "cl_doygun", labelKey: "FilterSaturated", css: "saturate(1.6) contrast(1.05)" },
      {
        key: "cl_kirsal",
        labelKey: "FilterRural",
        css: "saturate(1.35) sepia(0.15) contrast(1.05)",
      },
      {
        key: "cl_retro",
        labelKey: "FilterRetro",
        css: "sepia(0.3) saturate(1.4) contrast(1.1) hue-rotate(-10deg)",
      },
      {
        key: "cl_gayret",
        labelKey: "FilterEffort",
        css: "saturate(1.5) contrast(1.15) brightness(1.03)",
      },
      {
        key: "cl_arya",
        labelKey: "FilterArya",
        css: "saturate(1.45) hue-rotate(-6deg) contrast(1.08)",
      },
      {
        key: "cl_sert",
        labelKey: "FilterHarsh",
        css: "contrast(1.3) saturate(1.4) brightness(0.98)",
      },
    ],
  },
  {
    titleKey: "FilterGroupSoft",
    presets: [
      {
        key: "sf_aura",
        labelKey: "FilterAura",
        css: "brightness(1.1) contrast(0.9) saturate(1.1)",
      },
      {
        key: "sf_ela",
        labelKey: "FilterEla",
        css: "brightness(1.05) contrast(0.92) saturate(0.95) sepia(0.1)",
      },
      {
        key: "sf_hayali",
        labelKey: "FilterDreamy",
        css: "brightness(1.12) contrast(0.85) saturate(1.05) blur(0.3px)",
      },
      {
        key: "sf_gul",
        labelKey: "FilterRose",
        css: "brightness(1.05) sepia(0.2) saturate(1.15) hue-rotate(-15deg)",
      },
      {
        key: "sf_okyanus",
        labelKey: "FilterOcean",
        css: "brightness(1.05) saturate(1.1) hue-rotate(20deg) contrast(0.95)",
      },
      {
        key: "sf_bulut",
        labelKey: "FilterCloud",
        css: "brightness(1.1) contrast(0.88) saturate(0.9)",
      },
    ],
  },
  {
    titleKey: "FilterGroupVintage",
    presets: [
      {
        key: "vt_vinto",
        labelKey: "FilterVinto",
        css: "sepia(0.35) contrast(0.9) saturate(0.85) brightness(1.05)",
      },
      {
        key: "vt_solma",
        labelKey: "FilterFade",
        css: "sepia(0.25) contrast(0.85) saturate(0.8) brightness(1.08)",
      },
      {
        key: "vt_eski",
        labelKey: "FilterOld",
        css: "sepia(0.5) contrast(0.95) saturate(0.7) brightness(1.02)",
      },
      {
        key: "vt_nostaljik",
        labelKey: "FilterNostalgic",
        css: "sepia(0.4) contrast(0.9) saturate(0.9) hue-rotate(-8deg)",
      },
      {
        key: "vt_ruya",
        labelKey: "FilterDream",
        css: "sepia(0.3) brightness(1.08) contrast(0.85) saturate(1.05)",
      },
      {
        key: "vt_retro",
        labelKey: "FilterRetro",
        css: "sepia(0.45) contrast(1.05) saturate(0.95) hue-rotate(-12deg)",
      },
    ],
  },
  {
    titleKey: "FilterGroupMono",
    presets: [
      { key: "mo_klasik", labelKey: "FilterClassic", css: "grayscale(1) contrast(1.05)" },
      {
        key: "mo_murekkep",
        labelKey: "FilterInk",
        css: "grayscale(1) contrast(1.4) brightness(0.95)",
      },
      {
        key: "mo_kapkara",
        labelKey: "FilterPitchBlack",
        css: "grayscale(1) contrast(1.6) brightness(0.85)",
      },
      {
        key: "mo_film",
        labelKey: "FilterFilm",
        css: "grayscale(1) sepia(0.1) contrast(1.1) brightness(1.05)",
      },
      {
        key: "mo_gazete",
        labelKey: "FilterNewspaper",
        css: "grayscale(1) contrast(1.3) brightness(1.1)",
      },
      {
        key: "mo_karatahta",
        labelKey: "FilterBlackboard",
        css: "grayscale(1) invert(1) contrast(1.1) brightness(0.9)",
      },
    ],
  },
  {
    titleKey: "FilterGroupColorBurst",
    presets: [
      // Vivid "colour pop" looks — punchy saturation/contrast without the wild
      // hue-rotate flips that turned the previews psychedelic. The tints stay
      // small (≤12°) so skin/scenery keep their real colours, just louder.
      {
        key: "rp_sinirsiz",
        labelKey: "FilterLimitless",
        css: "saturate(1.9) contrast(1.15) brightness(1.03)",
      },
      {
        key: "rp_sicakhava",
        labelKey: "FilterHeatwave",
        css: "saturate(1.8) sepia(0.15) contrast(1.12) brightness(1.05)",
      },
      {
        key: "rp_ametist",
        labelKey: "FilterAmethyst",
        css: "saturate(1.7) hue-rotate(-12deg) contrast(1.1) brightness(1.03)",
      },
      {
        key: "rp_nanemsi",
        labelKey: "FilterMinty",
        css: "saturate(1.7) hue-rotate(12deg) contrast(1.1) brightness(1.03)",
      },
      {
        key: "rp_hatmi",
        labelKey: "FilterHollyhock",
        css: "saturate(1.8) hue-rotate(-8deg) contrast(1.12) brightness(1.04)",
      },
      {
        key: "rp_afis",
        labelKey: "FilterPoster",
        css: "contrast(1.4) saturate(1.9) brightness(1.02)",
      },
    ],
  },
  {
    titleKey: "FilterGroupCinematic",
    presets: [
      {
        key: "cn_teal",
        labelKey: "FilterTeal",
        css: "contrast(1.15) saturate(1.1) hue-rotate(8deg) brightness(0.98)",
      },
      {
        key: "cn_noirblue",
        labelKey: "FilterNoirBlue",
        css: "contrast(1.2) saturate(0.9) hue-rotate(15deg) brightness(0.95)",
      },
      {
        key: "cn_amber",
        labelKey: "FilterAmber",
        css: "sepia(0.25) contrast(1.1) saturate(1.2) brightness(1.03)",
      },
      {
        key: "cn_thriller",
        labelKey: "FilterThriller",
        css: "contrast(1.25) saturate(0.85) brightness(0.96)",
      },
      {
        key: "cn_western",
        labelKey: "FilterWestern",
        css: "sepia(0.35) contrast(1.15) saturate(1.05) brightness(1.02)",
      },
      {
        key: "cn_epic",
        labelKey: "FilterEpic",
        css: "contrast(1.2) saturate(1.15) hue-rotate(-5deg) brightness(1.0)",
      },
    ],
  },
  {
    titleKey: "FilterGroupPastel",
    presets: [
      {
        key: "ps_pembe",
        labelKey: "FilterPastelPink",
        css: "brightness(1.12) contrast(0.9) saturate(0.85) sepia(0.12) hue-rotate(-10deg)",
      },
      {
        key: "ps_mavi",
        labelKey: "FilterPastelBlue",
        css: "brightness(1.1) contrast(0.9) saturate(0.8) hue-rotate(15deg)",
      },
      {
        key: "ps_mint",
        labelKey: "FilterPastelMint",
        css: "brightness(1.1) contrast(0.9) saturate(0.85) hue-rotate(8deg)",
      },
      {
        key: "ps_lavanta",
        labelKey: "FilterPastelLavender",
        css: "brightness(1.1) contrast(0.9) saturate(0.85) hue-rotate(-15deg)",
      },
      {
        key: "ps_seftali",
        labelKey: "FilterPastelPeach",
        css: "brightness(1.1) contrast(0.92) saturate(0.95) sepia(0.15)",
      },
      {
        key: "ps_puf",
        labelKey: "FilterPastelPowder",
        css: "brightness(1.14) contrast(0.85) saturate(0.8)",
      },
    ],
  },
  {
    titleKey: "FilterGroupNeon",
    presets: [
      {
        key: "nn_siber",
        labelKey: "FilterCyber",
        css: "saturate(1.8) contrast(1.2) hue-rotate(-20deg) brightness(1.05)",
      },
      {
        key: "nn_elektrik",
        labelKey: "FilterElectric",
        css: "saturate(2) contrast(1.15) hue-rotate(20deg) brightness(1.08)",
      },
      {
        key: "nn_magenta",
        labelKey: "FilterNeonMagenta",
        css: "saturate(1.9) contrast(1.18) hue-rotate(-30deg) brightness(1.05)",
      },
      {
        key: "nn_asit",
        labelKey: "FilterAcid",
        css: "saturate(1.9) contrast(1.2) hue-rotate(40deg) brightness(1.06)",
      },
      {
        key: "nn_plazma",
        labelKey: "FilterPlasma",
        css: "saturate(2) contrast(1.25) hue-rotate(-45deg) brightness(1.04)",
      },
      {
        key: "nn_lazer",
        labelKey: "FilterLaser",
        css: "saturate(1.85) contrast(1.15) hue-rotate(60deg) brightness(1.07)",
      },
    ],
  },
];

export const FILTER_PRESETS = FILTER_GROUPS.flatMap((g) => g.presets);
const filterCssFor = (key) => FILTER_PRESETS.find((p) => p.key === key)?.css || "";

// Default intensity (%) for a freshly picked preset — full strength.
const DEFAULT_FILTER_INTENSITY = 100;

// No-op value each CSS filter function relaxes to. Scaling a preset toward these
// at intensity 0 leaves the image untouched.
const FILTER_IDENTITY = {
  brightness: 1,
  contrast: 1,
  saturate: 1,
  "hue-rotate": 0,
  grayscale: 0,
  sepia: 0,
  invert: 0,
  blur: 0,
  opacity: 1,
};

// Dial a preset's strength down by interpolating every filter function toward
// its identity value: out = identity + (value - identity) * t, t = intensity/100.
// t >= 1 returns the css untouched; t <= 0 collapses to identity (no visible effect).
function scaleFilterCss(css, t) {
  if (!css) return "";
  if (t >= 1) return css;
  return css.replace(/([a-z-]+)\(([-\d.]+)([a-z%]*)\)/g, (_m, fn, num, unit) => {
    const id = FILTER_IDENTITY[fn] ?? 0;
    const val = id + (parseFloat(num) - id) * Math.max(0, t);
    return `${fn}(${+val.toFixed(4)}${unit})`;
  });
}

// Fold the Adjust-slider filter and the chosen preset (dialled to `intensity`%)
// into one CSS string. Returns "none" when neither contributes anything.
function composeFilterCss(adjustVals, presetKey, intensity = DEFAULT_FILTER_INTENSITY) {
  const parts = [];
  const adj = buildFilterCss(adjustVals);
  if (adj !== "none") parts.push(adj);
  const preset = scaleFilterCss(filterCssFor(presetKey), intensity / 100);
  if (preset) parts.push(preset);
  return parts.length ? parts.join(" ") : "none";
}

// Crop aspect presets. null = free draw/resize. Value is w / h.
const CROP_RATIOS = [
  ["1:1", 1],
  ["16:9", 16 / 9],
  ["9:16", 9 / 16],
  ["4:3", 4 / 3],
  ["3:4", 3 / 4],
  ["5:4", 5 / 4],
  ["4:5", 4 / 5],
];

const BORDER_STYLES = [
  "none",
  "hidden",
  "dotted",
  "dashed",
  "solid",
  "double",
  "groove",
  "ridge",
  "inset",
  "outset",
];

// Quick-border presets shown as clickable swatches at the top of the Border
// section. Each preset writes a bundle of style keys at once (width/style/colour
// + 4-corner radius) so a good-looking border is one click away. `radius` is a
// single number applied to all four corners. A big radius (999) makes a pill.
const QUICK_BORDER_PRESETS = [
  // setWidth: bu presetler kalınlığı HEP yazar (border aç/kapa presetleri).
  // None → 0 (kaldır), Sharp → 5 (kalın keskin çerçeve). Şekil presetleri
  // (rounded/soft/...) setWidth taşımaz → kullanıcının kalınlığını korur.
  {
    key: "none",
    labelKey: "None",
    width: 0,
    style: "solid",
    color: "#000000",
    radius: 0,
    setWidth: true,
  },
  {
    key: "sharp",
    labelKey: "Sharp",
    width: 5,
    style: "solid",
    color: "#334155",
    radius: 0,
    setWidth: true,
  },
  { key: "rounded", labelKey: "Rounded", width: 2, style: "solid", color: "#334155", radius: 12 },
  { key: "soft", labelKey: "Soft", width: 2, style: "solid", color: "#334155", radius: 24 },
  { key: "pill", labelKey: "Pill", width: 2, style: "solid", color: "#334155", radius: 999 },
  // Gerçek daire: kutuyu 1:1 kareye çevirir (applyBorderPreset) + tüm köşelere
  // "50%" radius uygular. Dikdörtgen kutuda düz "50%" elips verir; `circle`
  // bayrağı kareleştirmeyi tetikler → her görselde tam yuvarlak.
  {
    key: "circle",
    labelKey: "Circle",
    width: 2,
    style: "solid",
    color: "#334155",
    radius: "50%",
    circle: true,
  },
  { key: "dashed", labelKey: "Dashed", width: 2, style: "dashed", color: "#334155", radius: 12 },
  { key: "dotted", labelKey: "Dotted", width: 2, style: "dotted", color: "#334155", radius: 12 },
];

function trainingIdFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get("id") || undefined;
  } catch {
    return undefined;
  }
}

// Pull the result image URL out of a completed job in the same priority order
// ImageAnimatorModal uses (result.url / result.imageUrl / result.images[].url).
function resultUrl(job) {
  const r = job?.result;
  if (!r) return null;
  if (typeof r.url === "string") return r.url;
  if (typeof r.imageUrl === "string") return r.imageUrl;
  if (Array.isArray(r.images) && r.images[0]?.url) return r.images[0].url;
  return null;
}

export default function ToolsPanel({
  boxId,
  box,
  imageUrl,
  navId,
  sceneIndex,
  styleState,
  structureState,
  dispatch,
  // "image" (varsayılan) tüm araçları gösterir. "video" yalnızca box'a
  // bağlı iki aracı — Kutu Stili (Border) ve Yapı (Structure) — açar; kırp /
  // arka plan sil / ayarlamalar / filtreler yalnızca görsele özeldir, gizlenir.
  variant = "image",
  onApplyImageUrl,
  onCropToNewBox,
  onInsufficientBalance,
  // İşaretçi Listesi (rich pluginler için) — Ayarlar panelinden buraya taşındı.
  showMarks = false,
  marksState,
  viewToolbars,
  onRichMarksModalToggled,
  onRichMarkEditPressed,
  onRichMarkDeleted,
  // İşaretçi Olayları (Pointer Events) — strip'teki aksiyondan Araçlar'a taşındı.
  // İçeriğe elle müdahaleyi (sürükle/etkileşim) açıp kapatan anlık toggle.
  showPointerEvents = false,
  pointerEventsEnabled = false,
  onTogglePointerEvents,
}) {
  const [activeTool, setActiveTool] = useState(null); // 'crop' | 'border' | 'removebg' | 'adjust' | 'structure' | null
  const [cropOpen, setCropOpen] = useState(false);
  const [cropAspect, setCropAspect] = useState(null); // locked w/h ratio, or null = free
  // CanvasCropOverlay's confirm + busy flag, driving the popover's ✕ / ✓.
  const cropConfirmRef = useRef(null);
  const [cropLoading, setCropLoading] = useState(false);
  const [chromaPickOpen, setChromaPickOpen] = useState(false);
  const [pickedColor, setPickedColor] = useState(null); // hex or null
  const [job, setJob] = useState(null); // { status, progress }
  const [error, setError] = useState(null);
  // Pointer Events toggle — durumu box'ın DOM class'ından türer; başka box
  // seçilince prop'tan senkronla.
  const [peOn, setPeOn] = useState(pointerEventsEnabled);
  useEffect(() => setPeOn(pointerEventsEnabled), [pointerEventsEnabled, boxId]);
  const pollRef = useRef(null);
  const panelRef = useRef(null); // the floating tool popover
  const stripRef = useRef(null); // the icon-button column (toggle source)

  const busy = !!job && job.status !== "completed" && job.status !== "failed";

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  // Herhangi bir araç (kırp / çerçeve / arka plan sil / ayarlar / filtre / yapı
  // / işaretçi) açıkken, seçili görselin üstündeki over-box aksiyon barını
  // (TopActionPill: görsel değiştir / ekrana sığdır / Aidea Wizard / daha fazla)
  // gizle — araç paneli ile çakışmasın. Sınıf yalnız bir araç seçiliyken durur.
  useEffect(() => {
    document.body.classList.toggle("tools-panel-open", !!activeTool);
    return () => document.body.classList.remove("tools-panel-open");
  }, [activeTool]);

  // Popover konumu render sırasında ölçülüyor; açıkken pencere boyutu ya da
  // timeline yüksekliği (sürükleme / daralt-genişlet) değişince yeniden hesapla.
  const [, setLayoutTick] = useState(0);
  useEffect(() => {
    if (!activeTool) return;
    const relayout = () => setLayoutTick((n) => n + 1);
    window.addEventListener("resize", relayout);
    const dock = document.getElementById("timelineDock");
    const ro = dock && typeof ResizeObserver !== "undefined" ? new ResizeObserver(relayout) : null;
    if (ro) ro.observe(dock);
    return () => {
      window.removeEventListener("resize", relayout);
      if (ro) ro.disconnect();
    };
  }, [activeTool]);

  // Kırp'tan başka bir araca geçilince kanvas kırpma çerçevesi otomatik kapansın
  // — kullanıcının çarpıya basması gerekmesin. (Diğer araç butonları yalnızca
  // activeTool'u değiştiriyordu, cropOpen açık kalıyordu.)
  useEffect(() => {
    if (activeTool !== "crop" && cropOpen) {
      setCropOpen(false);
      setCropAspect(null);
    }
  }, [activeTool, cropOpen]);

  // Close the active tool popover, resetting transient selections.
  const closeActiveTool = useCallback(() => {
    setActiveTool(null);
    setCropOpen(false);
    setCropAspect(null);
    setPickedColor(null);
  }, []);

  // Escape / click-outside closes the popover — mirrors LayersPanel. Skipped
  // while a canvas overlay (crop frame or colour pipette) is up, so drawing on
  // the canvas doesn't dismiss the tool. Capture phase because the popover
  // stops mousedown propagation.
  useEffect(() => {
    if (!activeTool) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !cropOpen && !chromaPickOpen) closeActiveTool();
    };
    const onDown = (e) => {
      if (cropOpen || chromaPickOpen) return;
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      const onStrip = stripRef.current && stripRef.current.contains(e.target);
      if (!inPanel && !onStrip) closeActiveTool();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown, true);
    };
  }, [activeTool, cropOpen, chromaPickOpen, closeActiveTool]);

  // ---- MW_API job flow (bg removal + chroma key) --------------------------
  const pollJob = useCallback(
    (jobId, attempt = 0) => {
      const apiUrl = process.env.MW_API_URL;
      if (attempt > 150) {
        setError(i18n.t("JobTimedOut"));
        setJob((j) => (j ? { ...j, status: "failed" } : j));
        return;
      }
      pollRef.current = setTimeout(async () => {
        try {
          const resp = await fetch(`${apiUrl}api/image-editing/job/${jobId}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const data = await resp.json();
          const status = data?.status;
          setJob({ status, progress: data?.progress ?? 0, result: data?.result });
          if (status === "completed") {
            const url = resultUrl(data);
            if (url) {
              onApplyImageUrl?.(url);
              // Applied in place → close the job's "Onay Bekliyor" widget card.
              approveJob(jobId);
              setJob(null);
            } else {
              setError(i18n.t("Result URL missing"));
            }
            return;
          }
          if (status === "failed" || status === "cancelled") {
            setError(i18n.t("JobFailed"));
            return;
          }
          pollJob(jobId, attempt + 1);
        } catch (err) {
          console.error("[ToolsPanel] poll failed:", err);
          pollJob(jobId, attempt + 1);
        }
      }, 4000);
    },
    [onApplyImageUrl]
  );

  const startJob = useCallback(
    async (endpoint, extraBody) => {
      const apiUrl = process.env.MW_API_URL;
      if (!apiUrl) {
        setError("MW_API_URL not configured");
        return;
      }
      setError(null);
      setJob({ status: "pending", progress: 0 });
      try {
        const body = {
          imageUrl,
          idealStudioTrainingId: trainingIdFromUrl(),
          sceneIndex,
          boxId,
          navId,
          ...extraBody,
        };
        const resp = await fetch(`${apiUrl}${endpoint}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          if (isCreditRefusal(errData)) {
            onInsufficientBalance?.(errData);
            setJob(null);
            return;
          }
          throw new Error(errData?.message || "request failed");
        }
        const data = await resp.json();
        if (data?.jobId) {
          setJob({ status: "processing", progress: 0 });
          pollJob(data.jobId, 0);
        } else {
          throw new Error("no jobId");
        }
      } catch (err) {
        console.error("[ToolsPanel] startJob failed:", err);
        setError(i18n.t("JobFailed"));
        setJob(null);
      }
    },
    [imageUrl, sceneIndex, boxId, navId, pollJob, onInsufficientBalance]
  );

  // ---- Crop ---------------------------------------------------------------
  const closeCrop = useCallback(() => {
    setCropOpen(false);
    setCropAspect(null);
    setActiveTool((t) => (t === "crop" ? null : t));
  }, []);

  const handleCropped = useCallback(
    async (blob, dims) => {
      // The cropped PNG is uploaded to this training's blob as a NEW file
      // (distinct name) — the source image's library asset is untouched, so the
      // library ends up holding both. A failed upload is rethrown so the crop
      // overlay stays open with the error and the user can retry.
      let url;
      try {
        url = await uploadCroppedBlob(blob, imageUrl);
      } catch (err) {
        console.error("[ToolsPanel] crop upload failed:", err);
        throw err;
      }
      try {
        // Fully decode BEFORE creating the new box, so it paints its image
        // immediately (the crop overlay stays up until then, hiding the swap).
        await new Promise((resolve) => {
          const im = new window.Image();
          const done = () => resolve();
          im.onload = () => (im.decode ? im.decode().then(done, done) : done());
          im.onerror = done;
          im.src = url;
        });
        // Clone the box with the crop as a NEW object at the crop rect and
        // delete the original (handled in EditorApp via the clipboard clone path).
        onCropToNewBox?.(url, dims);
        window.dispatchEvent(new Event("aidea:libraryChanged"));
      } catch (err) {
        console.error("[ToolsPanel] crop failed:", err);
        setError(i18n.t("CropFailed"));
      } finally {
        closeCrop();
      }
    },
    [imageUrl, onCropToNewBox, closeCrop]
  );

  // Restore the box to its pre-crop image + geometry. The snapshot is stashed
  // on structure.__cropOriginal by Clipboard.cropToNewBox; restoring puts the
  // full original image back, resizes/repositions the box to its original rect
  // and clears the marker so the button disappears.
  const cropOriginal = structureState?.__cropOriginal || null;
  const handleRestoreOriginal = useCallback(() => {
    const snap = structureState?.__cropOriginal;
    if (!snap) return;
    if (snap.url) onApplyImageUrl?.(snap.url);
    const resize = {};
    if (snap.width != null) {
      resize.width = snap.width;
      resize.widthUnit = snap.widthUnit || "%";
    }
    if (snap.height != null) {
      resize.height = snap.height;
      resize.heightUnit = snap.heightUnit || "%";
    }
    if (snap.x != null) resize.x = snap.x;
    if (snap.y != null) resize.y = snap.y;
    if (Object.keys(resize).length) dispatch(resizeBox(boxId, resize));
    dispatch(updatePluginToolbar(boxId, "main", "structure", "__cropOriginal", null));
    closeCrop();
  }, [boxId, dispatch, structureState, onApplyImageUrl, closeCrop]);

  // ---- Remove background: pick a colour first (optional), then confirm ----
  // Picking a colour only STORES it; nothing fires until "Onayla".
  const handleColorPicked = useCallback((hex) => {
    setChromaPickOpen(false);
    setPickedColor(hex);
  }, []);

  // Confirm: colour picked → chroma-key removal; no colour → plain bg removal.
  const handleConfirmRemoveBg = useCallback(() => {
    if (pickedColor) {
      startJob("api/image-editing/chroma-key-remove-background", {
        targetColor: pickedColor,
        tolerance: CHROMA_TOLERANCE,
      });
    } else {
      startJob("api/image-editing/remove-background", {});
    }
    setActiveTool(null);
    setPickedColor(null);
  }, [pickedColor, startJob]);

  // ---- Image adjustments (filters) ---------------------------------------
  // Current raw values come straight from structure.imageAdjust (falls back to
  // defaults). Each change writes both the raw object (structure) and the
  // recomputed CSS filter (style) so the box updates live.
  const adjust = { ...DEFAULT_ADJUST, ...(structureState?.imageAdjust || {}) };
  // Currently selected colour-filter preset ("none" = original). Adjust sliders
  // and the preset both feed the same style.filter, so every write recomposes
  // the combined string from whichever of the two is unchanged.
  const filterPreset = structureState?.filterPreset || "none";
  // Preset strength (0–100 %). Defaults to full; lowering it blends the preset
  // toward the original image in composeFilterCss.
  const filterIntensity =
    structureState?.filterIntensity == null
      ? DEFAULT_FILTER_INTENSITY
      : structureState.filterIntensity;
  const setAdjust = useCallback(
    (key, val) => {
      const next = { ...DEFAULT_ADJUST, ...(structureState?.imageAdjust || {}), [key]: val };
      dispatch(updatePluginToolbar(boxId, "main", "structure", "imageAdjust", next));
      dispatch(
        updatePluginToolbar(
          boxId,
          "main",
          "style",
          "filter",
          composeFilterCss(
            next,
            structureState?.filterPreset || "none",
            structureState?.filterIntensity == null
              ? DEFAULT_FILTER_INTENSITY
              : structureState.filterIntensity
          )
        )
      );
    },
    [boxId, dispatch, structureState]
  );
  const resetAdjust = useCallback(() => {
    dispatch(updatePluginToolbar(boxId, "main", "structure", "imageAdjust", { ...DEFAULT_ADJUST }));
    // "none" (not "") — an empty string is coerced to a bogus "0em" by the box
    // style loop; "none" is a valid CSS filter that renders nothing. Reset only
    // clears the sliders; the chosen preset stays.
    dispatch(
      updatePluginToolbar(
        boxId,
        "main",
        "style",
        "filter",
        composeFilterCss(
          DEFAULT_ADJUST,
          structureState?.filterPreset || "none",
          structureState?.filterIntensity == null
            ? DEFAULT_FILTER_INTENSITY
            : structureState.filterIntensity
        )
      )
    );
  }, [boxId, dispatch, structureState]);
  const adjustDirty = buildFilterCss(adjust) !== "none";

  /**
   * Filtreler reset — drops the preset back to "none" and its strength back to
   * full, then recomposes style.filter.
   *
   * Mirror image of resetAdjust: that one clears the sliders and keeps the
   * preset, this one clears the preset and keeps the sliders. Each panel
   * resets only what it owns, so neither can silently undo the other's work.
   */
  const resetFilterPreset = useCallback(() => {
    dispatch(updatePluginToolbar(boxId, "main", "structure", "filterPreset", "none"));
    dispatch(
      updatePluginToolbar(boxId, "main", "structure", "filterIntensity", DEFAULT_FILTER_INTENSITY)
    );
    dispatch(
      updatePluginToolbar(
        boxId,
        "main",
        "style",
        "filter",
        composeFilterCss(
          { ...DEFAULT_ADJUST, ...(structureState?.imageAdjust || {}) },
          "none",
          DEFAULT_FILTER_INTENSITY
        )
      )
    );
  }, [boxId, dispatch, structureState]);
  const filterDirty = filterPreset !== "none";

  // Apply a colour-filter preset ("none" clears it). Keeps the manual Adjust
  // sliders intact — the two stack in the composed filter string. Picking a
  // preset resets its intensity to full (100 %).
  const applyFilterPreset = useCallback(
    (key) => {
      const next = key === filterPreset ? "none" : key; // click active → toggle off
      dispatch(updatePluginToolbar(boxId, "main", "structure", "filterPreset", next));
      dispatch(
        updatePluginToolbar(boxId, "main", "structure", "filterIntensity", DEFAULT_FILTER_INTENSITY)
      );
      dispatch(
        updatePluginToolbar(
          boxId,
          "main",
          "style",
          "filter",
          composeFilterCss(
            { ...DEFAULT_ADJUST, ...(structureState?.imageAdjust || {}) },
            next,
            DEFAULT_FILTER_INTENSITY
          )
        )
      );
    },
    [boxId, dispatch, filterPreset, structureState]
  );

  // Live intensity change — recompose with the new strength, keep sliders.
  const setFilterIntensity = useCallback(
    (val) => {
      const v = clamp(Number(val), 0, 100);
      dispatch(updatePluginToolbar(boxId, "main", "structure", "filterIntensity", v));
      dispatch(
        updatePluginToolbar(
          boxId,
          "main",
          "style",
          "filter",
          composeFilterCss(
            { ...DEFAULT_ADJUST, ...(structureState?.imageAdjust || {}) },
            structureState?.filterPreset || "none",
            v
          )
        )
      );
    },
    [boxId, dispatch, structureState]
  );

  // ---- Border / box style -------------------------------------------------
  // Mirror the full "Kutu stili" section from the settings panel: 4-way
  // padding, background colour (with alpha), border width/style/colour, 4-way
  // corner radius and opacity. Writes to the same style state via
  // updatePluginToolbar so both panels stay in sync.
  const s = styleState || {};
  const padFallback = s.padding ?? 0;
  const radFallback = s.borderRadius ?? 0;
  const border = {
    paddingLeft: s.paddingLeft ?? padFallback,
    paddingRight: s.paddingRight ?? padFallback,
    paddingTop: s.paddingTop ?? padFallback,
    paddingBottom: s.paddingBottom ?? padFallback,
    backgroundColor: s.backgroundColor ?? "rgba(255,255,255,0)",
    borderWidth: s.borderWidth ?? 0,
    borderStyle: s.borderStyle ?? "solid",
    borderColor: s.borderColor ?? "#000000",
    borderTopLeftRadius: s.borderTopLeftRadius ?? radFallback,
    borderTopRightRadius: s.borderTopRightRadius ?? radFallback,
    borderBottomLeftRadius: s.borderBottomLeftRadius ?? radFallback,
    borderBottomRightRadius: s.borderBottomRightRadius ?? radFallback,
    opacity: s.opacity ?? 1,
  };
  const setBorder = (key, value) =>
    dispatch(updatePluginToolbar(boxId, "main", "style", key, value));

  // Apply a quick-border preset: write style + all four corner radii in one go.
  // Leaves padding/background/opacity AND the user's border width + colour
  // untouched — preset sadece şekli (stil + köşe yuvarlaklığı) uygular,
  // kullanıcının seçtiği kalınlık ve rengi ezmez.
  const applyBorderPreset = (p) => {
    // Tüm preset anahtarlarını TEK updatePluginToolbar ile yaz (name/value dizi
    // formu). Böylece preset tek atomik undo adımı olur — yanlış tıklamada tek
    // Ctrl+Z ile geri alınır. padding/arka plan/saydamlık ve borderColor burada
    // yer almadığından dokunulmadan korunur.
    // borderWidth kuralı: setWidth'li presetler (None/Sharp) kalınlığı HEP yazar
    // → None border'ı kaldırır (0), Sharp kalın keskin çerçeve verir (5). Şekil
    // presetleri kullanıcının kalınlığını KORUR; ama mevcut kalınlık 0 ise preset
    // varsayılanını yazar ki şekil görünür olsun. Kullanıcının girdiği kalınlık
    // (setWidth olmayan presetlerde) ezilmez.
    const curW = Number(border.borderWidth) || 0;
    const names = [
      "borderStyle",
      "borderTopLeftRadius",
      "borderTopRightRadius",
      "borderBottomLeftRadius",
      "borderBottomRightRadius",
    ];
    const values = [p.style, p.radius, p.radius, p.radius, p.radius];
    if (p.setWidth || curW === 0) {
      names.push("borderWidth");
      values.push(p.width);
    }
    dispatch(updatePluginToolbar(boxId, "main", "style", names, values));
    // Tam yuvarlak: kutuyu görsel olarak kareye çevir. width % kanvas
    // genişliğine, height % kanvas yüksekliğine oranlı olduğundan piksel kare
    // için height% = width% * canvasRatio (getMediaBoxSize ile aynı mantık).
    if (p.circle) {
      const st2 = structureState || {};
      const cRatio = (typeof window !== "undefined" && window.canvasRatio) || 16 / 9;
      const wPct = parseFloat(st2.width);
      const basePct = Number.isFinite(wPct) && wPct > 0 ? wPct : 44;
      const sidePct = Math.min(100, Math.max(1, Math.round(basePct * cRatio)));
      dispatch(
        resizeBox(boxId, {
          aspectRatio: false,
          width: basePct,
          widthUnit: "%",
          height: sidePct,
          heightUnit: "%",
        })
      );
    }
  };

  // A preset is "active" when the current style already matches its bundle.
  // radius sayı ("12") ya da yüzde ("50%") olabilir → String ile karşılaştır.
  const radiusEq = (v, r) => String(v) === String(r);
  const isPresetActive = (p) =>
    (!p.setWidth || Number(border.borderWidth) === p.width) &&
    border.borderStyle === p.style &&
    radiusEq(border.borderTopLeftRadius, p.radius) &&
    radiusEq(border.borderTopRightRadius, p.radius) &&
    radiusEq(border.borderBottomLeftRadius, p.radius) &&
    radiusEq(border.borderBottomRightRadius, p.radius);

  // Reset every border/box-style field back to its default in one atomic step.
  // Kullanıcı "Sıfırla" ile elle yaptığı tüm kenarlık ayarlarını temizler.
  // Defaults, yukarıdaki `border` fallback değerleriyle birebir aynı.
  const resetBorder = () =>
    dispatch(
      updatePluginToolbar(
        boxId,
        "main",
        "style",
        [
          "paddingLeft",
          "paddingRight",
          "paddingTop",
          "paddingBottom",
          "backgroundColor",
          "borderWidth",
          "borderStyle",
          "borderColor",
          "borderTopLeftRadius",
          "borderTopRightRadius",
          "borderBottomLeftRadius",
          "borderBottomRightRadius",
          "opacity",
        ],
        [0, 0, 0, 0, "rgba(255,255,255,0)", 0, "solid", "#000000", 0, 0, 0, 0, 1]
      )
    );

  // ---- Structure ("Yapı") -------------------------------------------------
  // Ported wholesale from the settings-panel Structure accordion
  // (core/editor/accordion_provider.es6): width/height with an aspect-ratio
  // lock, X/Y position and rotation + horizontal/vertical flip. Same actions:
  // size + position → resizeBox (writes box.position.x/y and structure);
  // rotation/flip → updatePluginToolbar(..., "structure", ...).
  const st = structureState || {};
  const rawAR = st.aspectRatio;
  const aspectLocked = rawAR === true || !!(rawAR && rawAR.checked === true);
  const numOrEmpty = (v) => (v === "auto" || v === undefined || v === null ? "" : parseInt(v, 10));
  const widthVal = numOrEmpty(st.width);
  const heightVal = numOrEmpty(st.height);
  const rotationVal = normalizeRotation(st.rotation);
  const hFlip = !!st.horizontalFlip;
  const vFlip = !!st.verticalFlip;
  const posX = box?.position?.x != null ? parseInt(box.position.x, 10) || 0 : 0;
  const posY = box?.position?.y != null ? parseInt(box.position.y, 10) || 0 : 0;

  const setWidth = (v) => dispatch(resizeBox(boxId, { width: v, widthUnit: st.widthUnit || "%" }));
  const setHeight = (v) =>
    dispatch(resizeBox(boxId, { height: v, heightUnit: st.heightUnit || "%" }));
  const setPosX = (v) => dispatch(resizeBox(boxId, { x: `${clamp(v, 0, 100)}%` }));
  const setPosY = (v) => dispatch(resizeBox(boxId, { y: `${clamp(v, 0, 100)}%` }));
  const setStructure = (key, val) =>
    dispatch(updatePluginToolbar(boxId, "main", "structure", key, val));
  const setRotation = (v) => setStructure("rotation", normalizeRotation(v));

  // Lock → height becomes "auto" (width stays editable); unlock → restore the
  // stashed height. Identical to renderUnifiedAspectRatioToggle.
  const toggleAspect = () => {
    const next = { aspectRatio: !aspectLocked };
    if (!aspectLocked) {
      if (st.height !== "auto") {
        next.__heightBeforeAuto = st.height;
        next.__heightUnitBeforeAuto = st.heightUnit;
      }
      next.height = "auto";
    } else if (st.height === "auto") {
      const prev = st.__heightBeforeAuto;
      next.height = typeof prev === "number" ? prev : 44;
      next.heightUnit = st.__heightUnitBeforeAuto || "%";
    }
    dispatch(resizeBox(boxId, next));
  };

  const resetTransform = () => {
    setRotation(0);
    if (hFlip) setStructure("horizontalFlip", false);
    if (vFlip) setStructure("verticalFlip", false);
  };
  /**
   * Yapı reset covers rotation + flips only.
   *
   * Width / height / X / Y are deliberately left alone: unlike a rotation,
   * they have no canonical "original" to return to — the box was authored at
   * whatever size and position the scene needs. Snapping them to some fixed
   * default would move the element rather than undo an adjustment, which is
   * not what a reset button is allowed to do.
   */
  const structureDirty = rotationVal !== 0 || hFlip || vFlip;

  // A %-unit number cell (width/height/x/y), matching the border `cell` look.
  const pctCell = (label, value, onChange, { disabled = false, unit = "%" } = {}) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={cellLabel}>{label}</div>
      <div style={{ ...cellBox, opacity: disabled ? 0.5 : 1 }}>
        <LiveNumberInput
          value={value}
          disabled={disabled}
          min={0}
          max={100}
          step={1}
          style={cellInput}
          onChange={onChange}
        />
        <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 2, flexShrink: 0 }}>{unit}</span>
      </div>
    </div>
  );

  /**
   * Full-width "Ayarları Sıfırla" that closes each tool panel.
   *
   * Every panel gets the same control in the same place, so "how do I undo
   * what I just did here" has one answer everywhere instead of being a
   * labelled button in Renk ayarları, a nameless icon in Yapı and nothing at
   * all in Filtreler / Kırp.
   *
   * `dirty` drives the disabled state: a reset that visibly does nothing is
   * worse than one that is greyed out, because the user cannot tell whether
   * the click registered.
   */
  const resetPanelButton = (dirty, onClick, title) => (
    <button
      type="button"
      disabled={!dirty}
      onClick={onClick}
      title={title}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        width: "100%",
        marginTop: 10,
        padding: "8px 10px",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        background: "#fff",
        color: dirty ? "#475569" : "#cbd5e1",
        fontSize: 12,
        fontWeight: 600,
        fontFamily: FONT,
        cursor: dirty ? "pointer" : "default",
      }}
    >
      <LuRotateCcw size={14} />
      {i18n.t("ResetAdjustments")}
    </button>
  );

  const flipBtn = (active, title, onClick, IconEl) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 30,
        height: 30,
        borderRadius: 6,
        border: "none",
        cursor: "pointer",
        padding: 0,
        flexShrink: 0,
        background: active ? "rgba(169,99,245,0.14)" : "transparent",
        color: active ? PURPLE : "#64748b",
      }}
    >
      {IconEl}
    </button>
  );

  const inputStyle = {
    width: "100%",
    padding: "4px 6px",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    fontSize: 11,
    fontFamily: FONT,
    boxSizing: "border-box",
  };
  const fieldLabel = {
    display: "block",
    fontSize: 10,
    fontWeight: 600,
    color: "#64748b",
    fontFamily: FONT,
    marginBottom: 3,
    marginTop: 6,
  };

  const sectionTitle = {
    fontSize: 10,
    fontWeight: 600,
    color: "#64748b",
    fontFamily: FONT,
    marginTop: 7,
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  };
  const cellLabel = {
    fontSize: 9,
    fontWeight: 600,
    color: "#94a3b8",
    fontFamily: FONT,
    marginBottom: 2,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  };
  const cellBox = {
    display: "flex",
    alignItems: "center",
    background: "#f1f5f9",
    borderRadius: 6,
    padding: "3px 6px",
    border: "1px solid #e2e8f0",
    flex: 1,
    minWidth: 0,
    boxSizing: "border-box",
  };
  const cellInput = {
    flex: 1,
    border: "none",
    background: "transparent",
    fontWeight: 600,
    fontSize: 12,
    color: "#1e293b",
    width: "100%",
    outline: "none",
    textAlign: "right",
    minWidth: 0,
    fontFamily: FONT,
  };

  const cell = (key, label, value) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={cellLabel}>{label}</div>
      <div style={cellBox}>
        <input
          type="number"
          value={value}
          min={0}
          max={100}
          step={1}
          style={cellInput}
          onChange={(e) =>
            setBorder(
              key,
              e.target.value === "" ? 0 : Math.min(100, Math.max(0, Number(e.target.value)))
            )
          }
        />
        <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: 2, flexShrink: 0 }}>px</span>
      </div>
    </div>
  );

  // ---- Adjustment slider row (Canva-style: label, track, value box) -------
  const adjustGroupTitle = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color: "#334155",
    fontFamily: FONT,
    margin: "12px 0 6px",
  };
  const adjustLabel = {
    fontSize: 11,
    fontWeight: 600,
    color: "#475569",
    fontFamily: FONT,
    marginBottom: 4,
  };
  const adjustValueBox = {
    width: 42,
    flexShrink: 0,
    padding: "3px 4px",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    color: "#1e293b",
    textAlign: "center",
    fontFamily: FONT,
    boxSizing: "border-box",
    outline: "none",
  };
  // Returns JSX (not a component) so re-renders don't remount the inputs.
  const adjustRow = (label, key, min = -100, max = 100) => (
    <div style={{ marginBottom: 10 }} key={key}>
      <div style={adjustLabel}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={adjust[key] ?? 0}
          onChange={(e) => setAdjust(key, Number(e.target.value))}
          style={{ flex: 1, accentColor: PURPLE, minWidth: 0 }}
        />
        <input
          type="number"
          min={min}
          max={max}
          value={adjust[key] ?? 0}
          onChange={(e) =>
            setAdjust(key, clamp(e.target.value === "" ? 0 : Number(e.target.value), min, max))
          }
          style={adjustValueBox}
        />
      </div>
    </div>
  );

  const icon = (Icon, on) => <Icon size={20} color={on ? PURPLE : IDLE} />;

  // Floating popover shell — mirrors LayersPanel (portal to <body>, fixed
  // bottom-right, header + close, scrollable body).
  const toolTitle =
    activeTool === "crop"
      ? i18n.t("Crop")
      : activeTool === "border"
        ? i18n.t("Border")
        : activeTool === "removebg"
          ? i18n.t("RemoveBackground")
          : activeTool === "adjust"
            ? i18n.t("ImageAdjustments")
            : activeTool === "filter"
              ? i18n.t("Filters")
              : activeTool === "structure"
                ? i18n.t("Structure")
                : activeTool === "marks"
                  ? i18n.t("marks.marks_list")
                  : "";
  // Anchor the popover just below the icon strip (the tool bar), right-aligned
  // to it — not pinned to the bottom of the screen. Alt sınır timeline'ın üst
  // kenarı: kısa ekranlarda popover timeline'a binmesin; şeridin altında yer
  // yoksa şeridin soluna açılır (bkz. toolsPopoverPlacement).
  const stripRect = stripRef.current?.getBoundingClientRect();
  const timelineDock = document.getElementById("timelineDock");
  const {
    top: popoverTop,
    right: popoverRight,
    maxHeight: popoverHeight,
  } = computeToolsPopoverPlacement({
    stripRect,
    timelineTop: timelineDock ? timelineDock.getBoundingClientRect().top : null,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    side: activeTool === "crop",
  });
  // Filtreler içeriği uzun olduğu için modal cap'e kadar uzayıp "Yapı"dan
  // büyük görünüyordu. Filtreler'i Yapı ile aynı kompakt yüksekliğe sabitle;
  // liste içeride scroll'lanır. Diğer araçlar içerik boyunca açılmaya devam eder.
  const STRUCTURE_HEIGHT = 275;
  const fixedFilterHeight =
    activeTool === "filter" || activeTool === "adjust" || activeTool === "border"
      ? Math.min(popoverHeight, STRUCTURE_HEIGHT)
      : undefined;
  // Kırp: oranlar alt alta — sağ panele paralel dar dikey sütun.
  const isCropTool = activeTool === "crop";
  const popoverStyle = {
    position: "fixed",
    top: popoverTop,
    right: popoverRight,
    width: isCropTool ? 60 : 268,
    height: fixedFilterHeight,
    maxHeight: popoverHeight,
    display: "flex",
    flexDirection: "column",
    background: "white",
    // Kırp: ikon kartıyla (#toolsPanelDrawer) aynı kenarsız kart görünümü.
    border: isCropTool ? "none" : "1px solid #e2e8f0",
    borderRadius: 14,
    boxShadow: isCropTool ? "0 4px 24px rgba(0,0,0,0.14)" : "0 8px 28px rgba(15,23,42,0.16)",
    // Kanvas zoom widget'ı (.canvasOverlay z-index:150) popover ile çakışıyordu;
    // butonları içeriğin üstüne biniyordu. Popover'ı onun üstüne al.
    zIndex: 160,
    overflow: "hidden",
  };
  const popoverHeader = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    padding: "9px 8px 9px 12px",
    borderBottom: "1px solid #f1f5f9",
    flexShrink: 0,
  };
  const popoverTitle = {
    flex: 1,
    fontSize: 12.5,
    fontWeight: 700,
    color: "#334155",
    fontFamily: FONT,
  };
  const popoverBody = { flex: 1, minHeight: 0, overflowY: "auto", padding: 10 };
  const cropActionBtn = {
    flex: 1,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: 7,
    padding: 0,
  };
  const cropDivider = { height: 1, background: "#f1f5f9", margin: "4px 2px" };
  const closeBtnStyle = {
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    color: "#64748b",
    flexShrink: 0,
    padding: 0,
  };

  return (
    <>
      {/* Icon-only vertical tool buttons (no text) */}
      <div
        ref={stripRef}
        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
      >
        {/* Kırp — yalnızca görselde */}
        {variant !== "video" && (
          <SceneIconButton
            label={i18n.t("Crop")}
            disabled={busy || !imageUrl}
            onClick={() => {
              const next = activeTool === "crop" ? null : "crop";
              setActiveTool(next);
              setCropOpen(next === "crop");
              if (next !== "crop") setCropAspect(null);
            }}
          >
            {icon(LuCrop, activeTool === "crop")}
          </SceneIconButton>
        )}
        <SceneIconButton
          label={i18n.t("Border")}
          disabled={busy}
          onClick={() => setActiveTool((t) => (t === "border" ? null : "border"))}
        >
          {icon(LuSquare, activeTool === "border")}
        </SceneIconButton>
        {/* Arka plan sil / Ayarlamalar / Filtreler — yalnızca görselde */}
        {variant !== "video" && (
          <>
            <SceneIconButton
              label={i18n.t("RemoveBackground")}
              disabled={busy || !imageUrl}
              onClick={() =>
                setActiveTool((t) => {
                  const next = t === "removebg" ? null : "removebg";
                  if (next !== "removebg") setPickedColor(null);
                  return next;
                })
              }
            >
              {icon(LuImageOff, activeTool === "removebg")}
            </SceneIconButton>
            <SceneIconButton
              label={i18n.t("ImageAdjustments")}
              disabled={busy || !imageUrl}
              onClick={() => setActiveTool((t) => (t === "adjust" ? null : "adjust"))}
            >
              {icon(LuSlidersHorizontal, activeTool === "adjust" || adjustDirty)}
            </SceneIconButton>
            <SceneIconButton
              label={i18n.t("Filters")}
              disabled={busy || !imageUrl}
              onClick={() => setActiveTool((t) => (t === "filter" ? null : "filter"))}
            >
              {icon(LuBlend, activeTool === "filter" || filterPreset !== "none")}
            </SceneIconButton>
          </>
        )}
        <SceneIconButton
          label={i18n.t("Structure")}
          disabled={busy}
          onClick={() => setActiveTool((t) => (t === "structure" ? null : "structure"))}
        >
          {icon(LuSettings, activeTool === "structure")}
        </SceneIconButton>
        {/* İşaretçi Listesi — yalnızca rich pluginlerde (ör. HotspotImages) */}
        {showMarks && (
          <SceneIconButton
            label={i18n.t("marks.marks_list")}
            disabled={busy}
            onClick={() => setActiveTool((t) => (t === "marks" ? null : "marks"))}
          >
            {icon(LuMapPin, activeTool === "marks")}
          </SceneIconButton>
        )}
        {/* İşaretçi Olayları — anlık toggle, alt panel açmaz. */}
        {showPointerEvents && (
          <SceneIconButton
            label={i18n.t("PointerEvents")}
            disabled={busy}
            onClick={() => {
              const enabled = onTogglePointerEvents?.();
              setPeOn(!!enabled);
            }}
          >
            {icon(LuMove, peOn)}
          </SceneIconButton>
        )}
      </div>

      {/* Busy / error indicators — icon only, no text */}
      {busy && (
        <LuLoaderCircle
          size={16}
          color={PURPLE}
          style={{ animation: "spin 1s linear infinite", marginTop: 2 }}
        />
      )}
      {error && (
        <span
          title={error}
          style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", marginTop: 2 }}
        />
      )}

      {/* All tool controls open in a floating popover portaled to <body>,
          matching the LayersPanel pattern (header + close, fixed bottom-right)
          rather than expanding inline inside the icon card. */}
      {activeTool &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={toolTitle}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={popoverStyle}
          >
            {/* Kırp kendi kompakt sütununu çizer (başlıksız); diğer araçlar
                başlık + kapat. */}
            {!isCropTool && (
              <div style={popoverHeader}>
                <span style={popoverTitle}>{toolTitle}</span>
                <button
                  type="button"
                  title={i18n.t("close", "Close")}
                  aria-label={i18n.t("close", "Close")}
                  onClick={closeActiveTool}
                  style={closeBtnStyle}
                >
                  <LuX size={17} />
                </button>
              </div>
            )}
            <div style={isCropTool ? { ...popoverBody, padding: 6 } : popoverBody}>
              {/* Crop — ikon şeridiyle aynı dilde dar dikey sütun: üstte ✕ iptal
                  / ✓ uygula (eskiden kanvasta çerçevenin üstündeydi), altında
                  oranlar. Free = manual drag; a ratio locks the on-canvas crop
                  frame to that shape. Ayrı "sıfırla" yok — "Serbest" aynı işi
                  görür. */}
              {isCropTool && (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      type="button"
                      title={i18n.t("Cancel")}
                      aria-label={i18n.t("Cancel")}
                      disabled={cropLoading}
                      onClick={closeActiveTool}
                      style={{
                        ...cropActionBtn,
                        background: "#f1f5f9",
                        color: "#475569",
                        cursor: cropLoading ? "default" : "pointer",
                      }}
                    >
                      <LuX size={15} />
                    </button>
                    <button
                      type="button"
                      title={i18n.t("Crop")}
                      aria-label={i18n.t("Crop")}
                      disabled={cropLoading || !cropOpen}
                      onClick={() => cropConfirmRef.current?.()}
                      style={{
                        ...cropActionBtn,
                        background: PURPLE,
                        color: "#fff",
                        cursor: cropLoading ? "wait" : "pointer",
                      }}
                    >
                      {cropLoading ? (
                        <LuLoaderCircle
                          size={14}
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                      ) : (
                        <LuCheck size={15} />
                      )}
                    </button>
                  </div>
                  <div style={cropDivider} />
                  {[["Free", null], ...CROP_RATIOS].map(([label, val]) => {
                    const active = cropAspect === val;
                    const text = val === null ? i18n.t("Free") : label;
                    return (
                      <button
                        key={label}
                        type="button"
                        title={text}
                        aria-pressed={active}
                        onClick={() => setCropAspect(val)}
                        onMouseEnter={(e) => {
                          if (!active) e.currentTarget.style.background = "rgba(0,0,0,0.05)";
                        }}
                        onMouseLeave={(e) => {
                          if (!active) e.currentTarget.style.background = "transparent";
                        }}
                        style={{
                          height: 26,
                          padding: "0 2px",
                          background: active ? "rgba(169,99,245,0.12)" : "transparent",
                          color: active ? PURPLE : "#475569",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 11,
                          fontWeight: active ? 700 : 600,
                          fontFamily: FONT,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          transition: "background 0.12s",
                        }}
                      >
                        {text}
                      </button>
                    );
                  })}
                  {/* Görseli orijinal haline döndür — yanlış kırpma sonrası
                      kırpmayı geri alıp orijinal (kırpılmamış) görseli geri
                      getirir. Sadece kırpılmış bir kutuda görünür. */}
                  {cropOriginal && (
                    <>
                      <div style={cropDivider} />
                      <button
                        type="button"
                        title={i18n.t("RestoreOriginalImageHint")}
                        aria-label={i18n.t("RestoreOriginalImage")}
                        onClick={handleRestoreOriginal}
                        style={{
                          ...cropActionBtn,
                          flex: "none",
                          height: 28,
                          background: "transparent",
                          color: "#475569",
                          cursor: "pointer",
                        }}
                      >
                        <LuRotateCcw size={16} />
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Border — box style: padding, bg colour, border, radius, opacity. */}
              {activeTool === "border" && (
                <>
                  {/* HIZLI ÇERÇEVE — one-click border presets (rounded etc.) */}
                  <div style={sectionTitle}>{i18n.t("QuickBorder")}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
                    {QUICK_BORDER_PRESETS.map((p) => {
                      const active = isPresetActive(p);
                      return (
                        <button
                          key={p.key}
                          type="button"
                          title={i18n.t(p.labelKey) || p.labelKey}
                          onClick={() => applyBorderPreset(p)}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 4,
                            width: 52,
                            padding: "6px 4px",
                            background: active ? "rgba(169,99,245,0.10)" : "#f8fafc",
                            border: `1px solid ${active ? PURPLE : "#e2e8f0"}`,
                            borderRadius: 8,
                            cursor: "pointer",
                            fontFamily: FONT,
                          }}
                        >
                          {/* Live swatch previewing the preset border/radius.
                              Circle → kare span + %50 radius = gerçek daire. */}
                          <span
                            style={{
                              width: p.circle ? 22 : 30,
                              height: 22,
                              boxSizing: "border-box",
                              background: "#fff",
                              borderStyle: p.width ? p.style : "solid",
                              borderWidth: p.width ? 2 : 1,
                              borderColor: p.width ? p.color : "#cbd5e1",
                              borderRadius: p.circle ? "50%" : Math.min(p.radius, 11),
                            }}
                          />
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 600,
                              color: active ? PURPLE : "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {i18n.t(p.labelKey) || p.labelKey}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* ALAN — 4-way padding (Left / Right / Top / Bottom) */}
                  <div style={sectionTitle}>{i18n.t("Padding")}</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    {cell("paddingLeft", i18n.t("Left") || "Left", border.paddingLeft)}
                    {cell("paddingRight", i18n.t("Right") || "Right", border.paddingRight)}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {cell("paddingTop", i18n.t("Top") || "Top", border.paddingTop)}
                    {cell("paddingBottom", i18n.t("Bottom") || "Bottom", border.paddingBottom)}
                  </div>

                  {/* Arka plan rengi — color + alpha */}
                  <label style={fieldLabel}>{i18n.t("BackgroundColor")}</label>
                  <ColorPicker
                    value={border.backgroundColor}
                    onChange={({ color }) => setBorder("backgroundColor", color)}
                  />

                  {/* Çerçeve genişliği */}
                  <label style={fieldLabel}>{i18n.t("BorderWidth")}</label>
                  <input
                    type="number"
                    min={0}
                    max={20}
                    value={border.borderWidth}
                    onChange={(e) => setBorder("borderWidth", Number(e.target.value))}
                    style={inputStyle}
                  />

                  {/* Çerçeve stili */}
                  <label style={fieldLabel}>{i18n.t("BorderStyle")}</label>
                  <select
                    value={border.borderStyle}
                    onChange={(e) => setBorder("borderStyle", e.target.value)}
                    style={inputStyle}
                  >
                    {BORDER_STYLES.map((bs) => (
                      <option key={bs} value={bs}>
                        {i18n.t(`styles.border_style_options.${bs}`) || bs}
                      </option>
                    ))}
                  </select>

                  {/* Çerçeve rengi — color + alpha */}
                  <label style={fieldLabel}>{i18n.t("BorderColor")}</label>
                  <ColorPicker
                    value={border.borderColor}
                    onChange={({ color }) => setBorder("borderColor", color)}
                  />

                  {/* RADYAN — 4-corner border radius (TL / TR / BL / BR) */}
                  <div style={sectionTitle}>{i18n.t("BorderRadius")}</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                    {cell("borderTopLeftRadius", "TL", border.borderTopLeftRadius)}
                    {cell("borderTopRightRadius", "TR", border.borderTopRightRadius)}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {cell("borderBottomLeftRadius", "BL", border.borderBottomLeftRadius)}
                    {cell("borderBottomRightRadius", "BR", border.borderBottomRightRadius)}
                  </div>

                  {/* Saydamlık */}
                  <label style={fieldLabel}>{i18n.t("Opacity")}</label>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={border.opacity}
                    onChange={(e) => setBorder("opacity", Number(e.target.value))}
                    style={{ ...inputStyle, padding: 0 }}
                  />

                  {/* SIFIRLA — tüm kenarlık/kutu-stili alanlarını varsayılana
                      döndürür. Preset seçimi renk vb. değerleri koruduğundan
                      "temizle" yolu bu tek buton. */}
                  <button
                    type="button"
                    onClick={resetBorder}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      width: "100%",
                      marginTop: 12,
                      padding: "7px 10px",
                      background: "#f8fafc",
                      color: "#475569",
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      fontSize: 11.5,
                      fontWeight: 600,
                      fontFamily: FONT,
                      cursor: "pointer",
                    }}
                  >
                    <LuRotateCcw size={14} />
                    {i18n.t("Reset")}
                  </button>
                </>
              )}

              {/* Remove background — optional chroma pick, then confirm. */}
              {activeTool === "removebg" && (
                <>
                  {/* Renk fonu sil — pipette row: pick a colour off the image */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <SceneIconButton
                      label={i18n.t("ChromaKey")}
                      disabled={busy || !imageUrl}
                      onClick={() => setChromaPickOpen(true)}
                    >
                      {icon(LuPipette, chromaPickOpen || !!pickedColor)}
                    </SceneIconButton>
                    <span style={{ fontSize: 12, fontFamily: FONT, color: "#475569" }}>
                      {i18n.t("ChromaKey")}
                    </span>
                    {pickedColor && (
                      <>
                        <span
                          title={pickedColor}
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 4,
                            border: "1px solid #cbd5e1",
                            background: pickedColor,
                            marginLeft: "auto",
                          }}
                        />
                        <button
                          type="button"
                          title={i18n.t("ClearPickedColor")}
                          onClick={() => setPickedColor(null)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#94a3b8",
                            cursor: "pointer",
                            fontSize: 14,
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >
                          ×
                        </button>
                      </>
                    )}
                  </div>

                  <p
                    style={{
                      fontSize: 10,
                      fontFamily: FONT,
                      color: "#94a3b8",
                      margin: "6px 0 8px",
                      lineHeight: 1.35,
                    }}
                  >
                    {i18n.t("RemoveBgHint")}
                  </p>

                  <button
                    type="button"
                    disabled={busy || !imageUrl}
                    onClick={handleConfirmRemoveBg}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      border: "none",
                      borderRadius: 8,
                      background: busy || !imageUrl ? "#cbd5e1" : PURPLE,
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: FONT,
                      cursor: busy || !imageUrl ? "default" : "pointer",
                    }}
                  >
                    {i18n.t("ConfirmRemoveBg")}
                  </button>
                </>
              )}

              {/* Image adjustments — Canva-style grouped sliders, applied live
                  as a CSS filter (non-destructive). */}
              {activeTool === "adjust" && (
                <>
                  {/* Beyaz dengesi */}
                  <div style={adjustGroupTitle}>{i18n.t("WhiteBalance")}</div>
                  {adjustRow(i18n.t("Temperature"), "temperature")}
                  {adjustRow(i18n.t("Tint"), "tint")}

                  {/* Aydınlık */}
                  <div style={adjustGroupTitle}>{i18n.t("Light")}</div>
                  {adjustRow(i18n.t("Brightness"), "brightness")}
                  {adjustRow(i18n.t("Contrast"), "contrast")}

                  {/* Renk */}
                  <div style={adjustGroupTitle}>{i18n.t("Color")}</div>
                  {adjustRow(i18n.t("Vibrance"), "vibrance")}
                  {adjustRow(i18n.t("Saturation"), "saturation")}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#475569",
                      fontFamily: FONT,
                      cursor: "pointer",
                      marginBottom: 4,
                    }}
                  >
                    {i18n.t("Invert")}
                    <input
                      type="checkbox"
                      checked={!!adjust.invert}
                      onChange={(e) => setAdjust("invert", e.target.checked)}
                      style={{ accentColor: PURPLE, width: 16, height: 16 }}
                    />
                  </label>

                  {/* Doku */}
                  <div style={adjustGroupTitle}>{i18n.t("Texture")}</div>
                  {adjustRow(i18n.t("Blur"), "blur", 0, 100)}

                  {resetPanelButton(adjustDirty, resetAdjust)}
                </>
              )}

              {/* Filters — one-click colour-look presets, grouped Natural / Warm
                  / Cold. Each swatch previews the real box image with the
                  preset's CSS filter applied; clicking writes structure.filterPreset
                  and recomposes style.filter. Click the active one to clear it. */}
              {activeTool === "filter" && (
                <>
                  {/* Yoğunluk — appears once a preset is picked. Sticks to the
                      top of the scroll area so it stays reachable while browsing
                      the grid. 100 % = full strength; lower blends toward original. */}
                  {filterPreset !== "none" && (
                    <div
                      style={{
                        position: "sticky",
                        top: -10,
                        zIndex: 1,
                        background: "#fff",
                        paddingBottom: 8,
                        marginBottom: 2,
                        borderBottom: "1px solid #f1f5f9",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 4,
                        }}
                      >
                        <span style={adjustLabel}>{i18n.t("FilterIntensity")}</span>
                        <span
                          style={{ fontSize: 11, fontWeight: 700, color: PURPLE, fontFamily: FONT }}
                        >
                          {filterIntensity}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={filterIntensity}
                        onChange={(e) => setFilterIntensity(e.target.value)}
                        style={{ width: "100%", accentColor: PURPLE }}
                      />
                    </div>
                  )}
                  {FILTER_GROUPS.map((group) => (
                    <div key={group.titleKey}>
                      <div style={sectionTitle}>{i18n.t(group.titleKey)}</div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        {group.presets.map((p) => {
                          const active = filterPreset === p.key;
                          return (
                            <button
                              key={p.key}
                              type="button"
                              title={i18n.t(p.labelKey) || p.labelKey}
                              onClick={() => applyFilterPreset(p.key)}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 4,
                                padding: 4,
                                background: active ? "rgba(169,99,245,0.10)" : "#fff",
                                border: `1px solid ${active ? PURPLE : "#e2e8f0"}`,
                                borderRadius: 8,
                                cursor: "pointer",
                                fontFamily: FONT,
                              }}
                            >
                              <span
                                style={{
                                  width: "100%",
                                  aspectRatio: "1 / 1",
                                  borderRadius: 5,
                                  overflow: "hidden",
                                  background: "#f1f5f9",
                                  display: "block",
                                }}
                              >
                                {imageUrl && (
                                  <img
                                    src={imageUrl}
                                    alt=""
                                    draggable={false}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      display: "block",
                                      filter: p.css || "none",
                                    }}
                                  />
                                )}
                              </span>
                              <span
                                style={{
                                  fontSize: 9,
                                  fontWeight: 600,
                                  color: active ? PURPLE : "#64748b",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "100%",
                                }}
                              >
                                {i18n.t(p.labelKey) || p.labelKey}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {resetPanelButton(filterDirty, resetFilterPreset)}
                </>
              )}

              {/* Structure — width/height + aspect lock, X/Y, rotation + flip.
                  Ported from the settings-panel "Yapı" accordion. */}
              {activeTool === "structure" && (
                <>
                  {/* Genişlik / Yükseklik */}
                  <div style={{ display: "flex", gap: 8 }}>
                    {pctCell(i18n.t("Width"), widthVal, setWidth)}
                    {pctCell(i18n.t("Height"), heightVal, setHeight, {
                      disabled: aspectLocked,
                      unit: aspectLocked ? i18n.t("Auto") : "%",
                    })}
                  </div>

                  {/* En boy oranını koru */}
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="aspectRatioLockHint">{i18n.t("AspectRatioLockHint")}</Tooltip>
                    }
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        marginTop: 10,
                        padding: "8px 10px",
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: 10,
                      }}
                    >
                      <label
                        onClick={toggleAspect}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#334155",
                          margin: 0,
                          flex: 1,
                          cursor: "pointer",
                          userSelect: "none",
                          fontFamily: FONT,
                        }}
                      >
                        {i18n.t("AspectRatioLock")}
                      </label>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={aspectLocked}
                        onClick={toggleAspect}
                        style={{
                          width: 38,
                          height: 22,
                          borderRadius: 11,
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          flexShrink: 0,
                          background: aspectLocked ? PURPLE : "#cbd5e1",
                          position: "relative",
                          transition: "background 0.15s ease",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: 2,
                            left: aspectLocked ? 18 : 2,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            background: "#fff",
                            transition: "left 0.15s ease",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                          }}
                        />
                      </button>
                    </div>
                  </OverlayTrigger>

                  {/* X / Y konum */}
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    {pctCell("X", posX, setPosX)}
                    {pctCell("Y", posY, setPosY)}
                  </div>

                  {/* Döndürme + çevirme */}
                  <div style={sectionTitle}>{i18n.t("Rotate")}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {/* Rotasyon pili: input + step */}
                    <div style={{ ...cellBox, flex: "none", gap: 4, padding: "3px 8px" }}>
                      <LiveNumberInput
                        value={rotationVal}
                        min={ROTATION_MIN}
                        max={ROTATION_MAX}
                        step={1}
                        style={{ ...cellInput, width: 40, textAlign: "center", flex: "none" }}
                        onChange={setRotation}
                      />
                      <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>°</span>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <HoldRepeatButton
                          onStep={() => setRotation(rotationVal + 1)}
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            padding: 0,
                            color: "#64748b",
                            display: "flex",
                          }}
                        >
                          <LuChevronUp size={12} />
                        </HoldRepeatButton>
                        <HoldRepeatButton
                          onStep={() => setRotation(rotationVal - 1)}
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            padding: 0,
                            color: "#64748b",
                            display: "flex",
                          }}
                        >
                          <LuChevronDown size={12} />
                        </HoldRepeatButton>
                      </div>
                    </div>

                    {/* Çevirme pili: sıfırla + yatay + dikey */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        background: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        padding: "3px 6px",
                      }}
                    >
                      {flipBtn(
                        false,
                        i18n.t("Reset", "Reset"),
                        resetTransform,
                        <LuRotateCcw size={16} />
                      )}
                      {flipBtn(
                        hFlip,
                        i18n.t("HorizontalFlip"),
                        () => setStructure("horizontalFlip", !hFlip),
                        <LuFlipHorizontal2 size={16} />
                      )}
                      {flipBtn(
                        vFlip,
                        i18n.t("VerticalFlip"),
                        () => setStructure("verticalFlip", !vFlip),
                        <LuFlipVertical2 size={16} />
                      )}
                    </div>
                  </div>

                  {resetPanelButton(structureDirty, resetTransform)}
                </>
              )}

              {/* İşaretçi Listesi — Ayarlar panelinden taşınan MarksList.
                  Ekle/düzenle işlemi mevcut RichMarksModal'ı tetikler. */}
              {activeTool === "marks" && showMarks && (
                <div className="marksListToolWrapper">
                  <MarksList
                    state={marksState}
                    viewToolbars={viewToolbars}
                    box_id={boxId}
                    onRichMarksModalToggled={onRichMarksModalToggled}
                    onRichMarkEditPressed={onRichMarkEditPressed}
                    onRichMarkDeleted={onRichMarkDeleted}
                  />
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

      {cropOpen && imageUrl && (
        <CanvasCropOverlay
          boxId={boxId}
          imageUrl={imageUrl}
          aspect={cropAspect}
          onCropped={handleCropped}
          onClose={closeCrop}
          confirmRef={cropConfirmRef}
          onLoadingChange={setCropLoading}
        />
      )}

      {chromaPickOpen && imageUrl && (
        <CanvasColorPickOverlay
          boxId={boxId}
          imageUrl={imageUrl}
          onPicked={handleColorPicked}
          onClose={() => setChromaPickOpen(false)}
        />
      )}
    </>
  );
}
